import React, { ChangeEventHandler, FC, ReactElement, useCallback, useState } from 'react';
import css from './settings.scss';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import { Button, TextInput, Snackbar, Dialog, SingleSelectMenu, Icon } from 'shamrock-clover-ui';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootState } from 'bootstrap';
import userService from '../../services/rtsproUser.service';
import sentinelUserService from '../../services/user.service';
import airshipService from '../../services/airship.service';
import { companyService } from '../../services/company.service';
import stmLogo from '../../assets/images/stm.png';
import stmpLogo from '../../assets/images/stmp.png';
import { userSchema } from '../../schemas/userSchema';
import { getClientIp, setUser } from '../../redux/actions/appActions';
import { getTrialState } from '../../redux/selectors/selectors';
import { User } from '../../interfaces/user';
import Permission from '../core/permission';
import FirebaseAnalytics from '../core/analytics';
import {
  getDeviceType,
  isBeingAddedField,
  isBeingEditedField,
  redirectToLogout,
  redirectToV2Logout,
} from '../../utils/utils';
import { DRIVER_ID, mustLogWhenAdded, mustLogWhenEdited, MY_REWARDS } from './constants';
import CheckIcon from '@mui/icons-material/Check';
import { useLanguageSelectionFeatureEnabled } from '../../hooks/useLanguageSelection';
import { useTranslation } from 'react-i18next';
import LanguageSelectionSpinner from '../core/LanguageSelectionSpinner';
import { SupportedLanguageDisplay, SupportedLanguages } from '../../i18n';
import classNames from 'classnames';
import { getAirshipChannelId } from '../../utils/airshipHandler';
import { useAuth0 } from '@auth0/auth0-react';

const Ajv = require('ajv');
const Config = require('Config');

const ERROR_NAME_TEXT = 'Please Enter A Valid Name';
const ERROR_PHONE_TEXT = 'Please Enter A Valid Phone Number';
const ERROR_PILOT_TEXT = 'Please Enter A Valid Pilot Number';

export const Settings = () => {
  const { i18n, t } = useTranslation();
  const ajv = new Ajv();
  const validate = ajv.compile(userSchema);
  const [showDialog, setShowDialog] = React.useState(false);
  const clientIp = useSelector(getClientIp, shallowEqual);
  const deviceId = localStorage.getItem('device_id');
  const { isAuthenticated: isAuthenticatedAuth0 } = useAuth0();
  const token = LocalAuth.getTokenValue();
  const email = LocalAuth.getUser().email as string;
  const authQuery = `?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;

  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguages>(i18n.language as SupportedLanguages);
  const [showLanguageChangeDialog, setShowLanguageChangeDialog] = React.useState(false);
  const dispatch = useDispatch();
  const [showLanguageLoader, setShowLanguageLoader] = useState(false);
  const [showLanguageSnackbar, setShowLanguageSnackbar] = useState(false);

  const userFromStore: User = useSelector((state: RootState) => state.app.user);

  const isShipper = (userFromStore as any).company_type === 'shipper';
  const features = useSelector((state: RootState) => state.app.features);
  const cpua3058web = features.includes('CPUA-3058-WEB');
  const cpua5206 = features.includes('CPUA-5206');
  const storeUser = JSON.parse(localStorage.getItem('trialUser'));
  const { isRegistered } = getTrialState();
  const isTrial = !isRegistered && cpua3058web;

  const [localUser, setLocalUser] = React.useState({
    pfjMyRewardsNumber: userFromStore?.pfjMyRewardsNumber ?? '',
    fullname: userFromStore?.firstname
      ? `${userFromStore.firstname} ${userFromStore.lastname}`
      : storeUser?.fullname ?? '',
    driverId: userFromStore?.driverId ?? '',
    phone: userFromStore?.phone ?? storeUser?.phone ?? '',
    company: userFromStore?.company ?? storeUser?.company ?? '',
  });

  const isLanguageSelectionFeatureEnabled = useLanguageSelectionFeatureEnabled();

  const handleSendAnalytics = async (event: string, label: string) => {
    const params = {
      [FirebaseAnalytics.PARAMETERS.BROWSER_TYPE]: getDeviceType(),
      [FirebaseAnalytics.PARAMETERS.DESCRIPTION]: label,
      [FirebaseAnalytics.PARAMETERS.DATETIMESTAMP]: Date.now().toString(),
      [FirebaseAnalytics.PARAMETERS.USER_IP]: clientIp,
    };
    FirebaseAnalytics.sendEvent(event, FirebaseAnalytics.MODULES.RTSPRO, FirebaseAnalytics.PAGES.SETTINGS, params);
  };

  const [showNameInput, setShowNameInput] = React.useState(false);
  const [showButtonPilot, setShowButtonPilot] = React.useState(false);
  const [disableSaveButton, setDisableSaveButton] = React.useState(true);
  const [disablePilot, setDisablePilot] = React.useState(true);
  const [pilotHasErrors, setPilotHasErrors] = React.useState(false);
  const [phoneHasErrors, setPhoneHasErrors] = React.useState(false);
  const [nameHasErrors, setNameHasErrors] = React.useState(false);
  const [showServiceResult, setShowServiceResult] = React.useState(OnSaveNotificationState.CLOSED);
  const [company, setCompany] = React.useState(null);
  const [showDeleteFail, setShowDeleteFail] = React.useState(false);

  React.useEffect(() => {
    if (!userFromStore.userStatus.isTrial && userFromStore.userStatus.isOnCompany) {
      companyService
        .getCompany(email)
        .then(([result]) => {
          setCompany(result);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, []);

  React.useEffect(() => {
    if (showLanguageLoader) {
      const timerRef = setTimeout(() => {
        setShowLanguageSnackbar(true);
        setShowLanguageLoader(false);
      }, 1000);

      return () => clearTimeout(timerRef);
    }
  }, [showLanguageLoader]);

  const changePilot = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value } }) => {
      const removeChars = value.replace(/[^0-9]/g, '');
      setLocalUser({ ...localUser, pfjMyRewardsNumber: removeChars });
      validate({ pfjMyRewardsNumber: removeChars });
      setDisablePilot(false);
      setPilotHasErrors(validate?.errors && validate.errors[0].dataPath === '.pfjMyRewardsNumber' ? true : false);
    },
    [localUser],
  );

  const changePhone = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value: valuePhone } }) => {
      const removeCharsPhone = valuePhone.replace(/[^0-9]/g, '');
      setLocalUser({ ...localUser, phone: removeCharsPhone });
      validate({ phone: removeCharsPhone });
      setDisableSaveButton(false);
      setPhoneHasErrors(validate?.errors && validate.errors[0].dataPath === '.phone' ? true : false);
    },
    [localUser],
  );

  const changeDriverId = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value: valueId } }) => {
      const removeCharsId = valueId.replace(/[^0-9]/g, '');
      setDisableSaveButton(false);
      setLocalUser({ ...localUser, driverId: removeCharsId });
    },
    [localUser],
  );

  const changeName = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value: valueName } }) => {
      const removeCharsName = valueName.replace(/[^a-zA-Z ]/g, '');
      setLocalUser({ ...localUser, fullname: removeCharsName });
      setDisableSaveButton(false);
      const [firstName = '', lastName = ''] = removeCharsName.split(' ', 2);
      validate({ firstname: firstName, lastname: lastName });
      setNameHasErrors(
        validate?.errors &&
          (validate?.errors[0]?.dataPath === '.firstname' || validate?.errors[0]?.dataPath === '.lastname')
          ? true
          : false,
      );
    },
    [localUser],
  );

  const changeCompany = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value: valueName } }) => {
      setLocalUser({ ...localUser, company: valueName });
      setDisableSaveButton(false);
    },
    [localUser],
  );

  const updatePilotNumber = async () => {
    try {
      setShowButtonPilot(false);
      setDisableSaveButton(true);
      await userService.updateUser({ email: email }, { pfjMyRewardsNumber: localUser.pfjMyRewardsNumber });
      dispatch(setUser({ ...userFromStore, pfjMyRewardsNumber: localUser.pfjMyRewardsNumber }));
      setShowServiceResult(OnSaveNotificationState.SUCCESS);
      handleSendAnalytics(FirebaseAnalytics.EVENTS.EDIT, FirebaseAnalytics.LABELS.EDITION_MY_REWARDS);
    } catch (e) {
      setShowServiceResult(OnSaveNotificationState.ERROR);
    }
  };

  const updateUserInfo = async () => {
    try {
      const [firstName, lastName] = localUser.fullname.split(' ', 2);
      const changedUser = {
        pfjMyRewardsNumber: localUser.pfjMyRewardsNumber,
        phone: localUser.phone,
        driverId: localUser.driverId,
        firstname: firstName,
        lastname: lastName,
        company: localUser.company,
      };
      if (isTrial) {
        const { pfjMyRewardsNumber, driverId, ...rest } = changedUser;
        await userService.updateTrialUser(email, rest);
        const user = {
          fullname: `${rest.firstname} ${rest.lastname}`,
          ...rest,
        };
        localStorage.setItem('trialUser', JSON.stringify(user));
      } else {
        const { company, ...rest } = changedUser;
        await userService.updateUser({ email }, rest);
        dispatch(setUser({ ...userFromStore, ...rest }));
      }
      setShowServiceResult(OnSaveNotificationState.SUCCESS);

      mustLogWhenAdded?.map((field) => {
        const analyticsData = getAnalyticsInfo(field);
        isBeingAddedField(localUser[field], userFromStore[field]) &&
          handleSendAnalytics(analyticsData.event, analyticsData.label);
      });
      mustLogWhenEdited?.map((field) => {
        const analyticsData = getAnalyticsInfo(field);
        isBeingEditedField(localUser[field], userFromStore[field]) &&
          handleSendAnalytics(analyticsData.event, analyticsData.label);
      });
    } catch (e) {
      setShowServiceResult(OnSaveNotificationState.ERROR);
    }
  };

  const cancelPilotUpdate = () => {
    setPilotHasErrors(false);
    setLocalUser({ ...localUser, pfjMyRewardsNumber: userFromStore?.pfjMyRewardsNumber ?? '' });
    setShowButtonPilot(false);
    setDisablePilot(true);
  };

  const cancelEditUser = () => {
    setLocalUser({
      ...localUser,
      driverId: userFromStore?.driverId ?? '',
      fullname: userFromStore?.firstname
        ? `${userFromStore.firstname} ${userFromStore.lastname}`
        : storeUser?.fullname ?? '',
      phone: userFromStore?.phone ?? storeUser?.phone ?? '',
      company: userFromStore?.company ?? storeUser?.company ?? '',
    });
    setShowNameInput(false);
    setNameHasErrors(false);
    setPhoneHasErrors(false);
    setDisableSaveButton(true);
  };

  const stmCard = [
    {
      shop_id: company?.shop_ids?.stm,
    },
    {
      shop_id: company?.shop_ids?.stmp,
    },
  ];

  const DeleteButtons = (props) => {
    const { userService, email, setShowDialog, setShowDeleteFail } = props;

    const handleDeleteAccount = async () => {
      try {
        await userService.deleteUser(email);
        isAuthenticatedAuth0 ? redirectToV2Logout() : redirectToLogout();
        setShowDialog(false);
        handleSendAnalytics(FirebaseAnalytics.EVENTS.CLICK, FirebaseAnalytics.LABELS.CONFIRM_DELETE_ACCOUNT);
      } catch (e) {
        setShowDialog(false);
        setShowDeleteFail(true);
      }
    };

    const logoutTrial = () => {
      redirectToLogout();
      setShowDialog(false);
    };

    return (
      <div className={css.actionButtonContainer}>
        <Button
          buttonStyle="outlined"
          onClick={() => {
            setShowDialog(false);
            handleSendAnalytics(FirebaseAnalytics.EVENTS.CLICK, FirebaseAnalytics.LABELS.CANCEL_DELETE_ACCOUNT);
          }}
        >
          {t('cancel')}
        </Button>
        <Button color="red" onClick={() => (isTrial ? logoutTrial() : handleDeleteAccount())}>
          {t('delete_account')}
        </Button>
      </div>
    );
  };

  const ChangeLanguageContent = () => {
    const selectedOption = SupportedLanguageDisplay.find((language) => language.languageKey === selectedLanguage);
    return (
      <>
        <h3 className={css.popUpHeader} style={{ marginBottom: '24px' }}>
          Language - idioma
        </h3>
        <div className={css.textBody}>Choose your language</div>
        <div className={css.textBody}>Elige tu idioma</div>
        <SingleSelectMenu
          fontSize="16px"
          menuLocation="below"
          VisualElement={
            VisualComponent({
              selectedLabel: selectedOption.displayName,
            }) as ReactElement
          }
          onOptionSelected={(option) => {
            const selectLanguage = SupportedLanguageDisplay.find(
              (language) => language.displayName === option.optionName,
            );
            if (option?.optionName && selectLanguage) setSelectedLanguage(selectLanguage.languageKey);
          }}
          menuOptions={SupportedLanguageDisplay.map((language) => ({
            optionName: language.displayName,
            value: language.languageKey,
          }))}
          selectedOption={{ ...selectedOption, optionName: selectedOption.displayName }}
          width="300px"
        />
      </>
    );
  };

  const SaveLanguageButton = () => {
    const handleSaveLanguage = async () => {
      try {
        setShowLanguageChangeDialog(false);
        setShowLanguageLoader(true);
        i18n.changeLanguage(selectedLanguage);
        const airshipChannelId = await getAirshipChannelId();
        cpua5206
          ? airshipService.updateDeviceInformation(airshipChannelId, email, selectedLanguage, deviceId)
          : airshipService.setDeviceLanguage(airshipChannelId, email, selectedLanguage);
      } catch (e) {
        setShowLanguageChangeDialog(false);
      }
    };

    return (
      <div className={css.actionButtonContainer}>
        <Button
          buttonStyle="outlined"
          onClick={() => {
            setShowLanguageChangeDialog(false);
          }}
        >
          {t('cancel')}
        </Button>
        <Button color="blue" disabled={selectedLanguage === i18n.language} onClick={() => handleSaveLanguage()}>
          {t('apply_changes')}
        </Button>
      </div>
    );
  };

  const languageDisplay = SupportedLanguageDisplay.find((x) => x.languageKey === i18n.language);

  return (
    <div className={css.wrapper}>
      {showLanguageLoader && <LanguageSelectionSpinner text={languageDisplay.changeText} />}
      <header className={css.header}>
        <h2>{t('settings')}</h2>
      </header>
      <main className={css.body}>
        <Dialog
          onClose={() => {
            setShowDialog(false);
          }}
          content={<DeletePopUp />}
          actions={
            <DeleteButtons
              userService={sentinelUserService}
              email={email}
              setShowDialog={setShowDialog}
              setShowDeleteFail={setShowDeleteFail}
            />
          }
          open={showDialog}
        />
        <Dialog
          onClose={() => {
            setShowLanguageChangeDialog(false);
          }}
          content={<ChangeLanguageContent />}
          actions={<SaveLanguageButton />}
          open={showLanguageChangeDialog}
        />
        <div className={css.main}>
          <div className={css.card}>
            <header className={css.headerBorder}>
              <h3 className={css.cardHeader}>{t('manage_your_rts_profile')}</h3>
            </header>
            <div className={css.mainContent}>
              {showNameInput || isTrial ? (
                <TextInput
                  id="name"
                  label={t('full_name')}
                  value={localUser.fullname}
                  onFocus={() => setShowNameInput(true)}
                  onChange={changeName}
                  error={nameHasErrors && ERROR_NAME_TEXT}
                  className={nameHasErrors ? css.errorProfileInput : css.profileInput}
                />
              ) : (
                <div className={css.nameTextContainer}>
                  <p className={css.nameText} onClick={() => setShowNameInput(true)}>
                    {localUser.fullname}
                  </p>
                </div>
              )}
              <TextInput
                id="phone"
                label={t('phone_number')}
                value={localUser.phone}
                className={phoneHasErrors ? css.errorProfileInput : css.profileInput}
                onFocus={() => setShowNameInput(true)}
                onChange={changePhone}
                maxLength={10}
                error={phoneHasErrors && ERROR_PHONE_TEXT}
              />
              {!isTrial && (
                <TextInput
                  id="driveId"
                  label={t('driverid_number_optional')}
                  value={localUser.driverId}
                  className={css.profileInput}
                  onFocus={() => setShowNameInput(true)}
                  onChange={changeDriverId}
                />
              )}
              <TextInput
                label={t('company_name')}
                value={localUser.company}
                className={css.profileInput}
                onFocus={() => setShowNameInput(true)}
                disabled={isTrial ? false : true}
                onChange={changeCompany}
                variant={!isTrial ? 'filled' : undefined}
              />
              <TextInput
                label={t('email')}
                value={email}
                className={css.profileInput}
                onFocus={() => setShowNameInput(true)}
                disabled={true}
                variant="filled"
              />
              {showNameInput && (
                <div className={css.userButtonContainer}>
                  <Button buttonStyle="outlined" width="180px" onClick={cancelEditUser}>
                    {t('cancel')}
                  </Button>
                  <Button
                    disabled={disableSaveButton || nameHasErrors || phoneHasErrors}
                    width="180px"
                    onClick={updateUserInfo}
                  >
                    {t('save_changes')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={css.secondary}>
          {!isTrial && !isShipper && (
            <div className={css.card}>
              <header className={css.headerBorder}>
                <h3 className={css.cardHeader}>PILOT</h3>
              </header>
              <div className={css.cardBody}>
                <p className={css.pilotText}>{t('providing_myrewards_number_help_us_connect')}</p>
                <div className={css.pilotTextInputContainer}>
                  <TextInput
                    id="pilot"
                    label={t('myrewards_number')}
                    value={localUser.pfjMyRewardsNumber}
                    onFocus={() => setShowButtonPilot(true)}
                    onChange={changePilot}
                    maxLength={14}
                    error={pilotHasErrors && ERROR_PILOT_TEXT}
                  ></TextInput>
                  {showButtonPilot && (
                    <div className={pilotHasErrors ? css.pilotErrorButtonContainer : css.pilotButtonContainer}>
                      <Button buttonStyle="outlined" width="180px" onClick={cancelPilotUpdate}>
                        {t('cancel')}
                      </Button>
                      <Button disabled={disablePilot || pilotHasErrors} width="180px" onClick={updatePilotNumber}>
                        {t('save_changes')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {!isTrial && !isShipper && (
            <div className={css.card}>
              <header className={css.headerBorder}>
                <h3 className={css.cardHeader}>{t('discount_numbers')}</h3>
              </header>
              <div className={css.discountContainer}>
                {stmCard.map((field, itr) => (
                  <div key={itr} className={css.stmContainer}>
                    <div className={css.stmImageContainer}>
                      <img className={css.img} src={itr === 0 ? stmLogo : stmpLogo} />
                    </div>
                    <div className={css.stmTextContainer}>
                      <p className={classNames(css.stmText, 'pltgbmask')}>
                        {userFromStore.firstname} {userFromStore.lastname}
                      </p>
                      <p className={classNames(css.stmText, 'pltgbmask')}>RTS ID: {company?.company_id}</p>
                      <p className={classNames(css.shopIdText, 'pltgbmask')}>
                        {field?.shop_id
                          ? `${field?.shop_id?.substr(0, 4)}-${field?.shop_id?.substr(4, 3)}`
                          : '9900-002'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Permission
            permission={[
              'carrierpro.PermissionsAdmin',
              'carrierpro.ViewRole',
              'carrierpro.CompanyAdmin',
              'carrierpro.RepCompanyAdmin',
            ]}
          >
            <div className={css.card}>
              <header className={css.headerBorder}>
                <h3 className={css.cardHeader}>{t('manage_your_rtspro_users')}</h3>
              </header>
              <p className={css.manageText}>{t('manage_existing_users_roles_or_invite')}</p>
              <div className={css.manageUserButton}>
                <Button
                  onClick={() => window.open(`${Config.adminUserMangementUrl}${authQuery}`, '_blank')}
                  buttonStyle="outlined"
                  color="blue"
                  width="100%"
                >
                  {t('manage_users')}
                </Button>
              </div>
            </div>
          </Permission>
          {isLanguageSelectionFeatureEnabled && !isShipper && (
            <div className={css.card}>
              <header className={css.headerBorder}>
                <h3 className={css.cardHeader}>LANGUAGE - IDIOMA</h3>
              </header>
              <div className={css.chooseLanguageContainer}>
                <div>
                  <p className={css.languageText}>Choose your language</p>
                  <p className={css.languageText}>Elige tu idioma</p>
                </div>
                <div className={css.languageSelectedContainer}>
                  <CheckIcon style={{ color: '#0091EA' }} />
                  <p className={css.languageText}>{languageDisplay.displayName}</p>
                </div>
              </div>
              <div className={css.changeLanguageButtonContainer}>
                <Button
                  onClick={() => {
                    setShowLanguageChangeDialog(true);
                  }}
                  width="100%"
                  buttonStyle="outlined"
                  color="blue"
                >
                  {t('change_language')}
                </Button>
              </div>
            </div>
          )}
          {!isShipper && (
            <div className={css.card}>
              <header className={css.headerBorder}>
                <h3 className={css.cardHeader}>{t('manage_your_account')}</h3>
              </header>
              <p className={css.deleteAccountText}>{t('this_action_permanently_deletes_your_account_and_app_data')}</p>
              <div className={css.deleteButtonContainer}>
                <Button
                  onClick={() => {
                    setShowDialog(true);
                    handleSendAnalytics(FirebaseAnalytics.EVENTS.CLICK, FirebaseAnalytics.LABELS.DELETE_ACCOUNT);
                  }}
                  width="100%"
                  buttonStyle="outlined"
                  color="red"
                >
                  {t('delete_account')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Snackbar
        message={onSaveMessageText[showServiceResult]}
        open={showServiceResult !== OnSaveNotificationState.CLOSED}
        onClose={() => setShowServiceResult(OnSaveNotificationState.CLOSED)}
        variant={showServiceResult === OnSaveNotificationState.SUCCESS ? 'success' : 'error'}
      />
      <Snackbar
        variant="success"
        duration={10000}
        open={showLanguageSnackbar}
        onClose={() => setShowLanguageSnackbar(false)}
        message={t('language_changed')}
      ></Snackbar>
      <Snackbar
        message={'An error occurred; please try again later.'}
        open={showDeleteFail}
        onClose={() => setShowDeleteFail(false)}
        variant="error"
      />
    </div>
  );
};

const DeletePopUp = () => {
  const { t } = useTranslation();
  return (
    <>
      <h3 className={css.popUpHeader}>{t('delete_account')}</h3>
      <p className={css.popUpBody}>{t('are_you_sure_you_want_to_delete_account_settings_confirmation')}</p>
    </>
  );
};

enum OnSaveNotificationState {
  CLOSED,
  SUCCESS,
  ERROR,
}

const onSaveMessageText = {
  [OnSaveNotificationState.CLOSED]: '',
  [OnSaveNotificationState.SUCCESS]: 'Changes successfully saved.',
  [OnSaveNotificationState.ERROR]: 'An error occurred saving the changes; please try again later.',
};

const getAnalyticsInfo = (field) => {
  switch (field) {
    case DRIVER_ID:
      return { label: FirebaseAnalytics.LABELS.ADD_DRIVER_ID, event: FirebaseAnalytics.EVENTS.ADD };
    case MY_REWARDS:
      return { label: FirebaseAnalytics.LABELS.EDITION_MY_REWARDS, event: FirebaseAnalytics.EVENTS.ADD };
    // this happens when editing to mark lastname, firstname and phone
    default:
      return { label: FirebaseAnalytics.LABELS.EDITION_ACCOUNT_INFO, event: FirebaseAnalytics.EVENTS.EDIT };
  }
};

interface VisualComponentProps {
  selectedLabel?: string;
  selected?: string;
}

const VisualComponent: FC<VisualComponentProps> = ({ selectedLabel }) => (
  <div className={css.dropdown}>
    <p>{selectedLabel}</p>
    <Icon icon="triangleDown" size="16" className={css.triangleDown} color="gray2" />
  </div>
);
